import type { FormEvent, ReactNode } from 'react';
import { forwardRef, memo, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { SxProps, Theme } from '@mui/material';
import { Button, Typography } from '@mui/material';
import { isMobileApp } from 'utils/common';
import { ButtonsWrapper, Content, Dialog, Form, HandleBar, Header } from './ActionModal.styles';

export interface Props {
  title: string | ReactNode;
  children: ReactNode;
  open: boolean;
  isOnSubmitDisabled?: boolean;
  onClose: () => void;
  onSubmit?: () => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  cancelText?: string;
  submitText?: string;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fullWidth?: boolean;
  fullScreen?: boolean;
  sx?: SxProps<Theme>;
  className?: string;
  submitIcon?: {
    icon: ReactNode;
    position: 'start' | 'end';
  };
  cancelIcon?: {
    icon: ReactNode;
    position: 'start' | 'end';
  };
  disableBackdropClick?: boolean;
  dataTestIds?: {
    dialog?: string;
    cancelButton?: string;
    submitButton?: string;
  };
}

const ActionModalBase = forwardRef<{ clickSubmit: () => void }, Props>(
  (
    {
      title,
      open,
      onClose,
      children,
      isSubmitting = false,
      onSubmit,
      onCancel,
      cancelText,
      submitText,
      maxWidth,
      fullWidth = true,
      fullScreen,
      isOnSubmitDisabled = false,
      submitIcon,
      cancelIcon,
      sx,
      className,
      disableBackdropClick = false,
      dataTestIds,
    }: Props,
    ref
  ) => {
    const { t } = useTranslation();
    const mobileApp = isMobileApp();

    const handleSubmit = (e: FormEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onSubmit?.();
    };

    const handleCancel = (e: FormEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onCancel?.();
    };

    const handleClose = (e: FormEvent, reason?: 'backdropClick' | 'escapeKeyDown') => {
      if (disableBackdropClick && reason === 'backdropClick') return;
      e.preventDefault();
      e.stopPropagation();
      onClose?.();
    };

    const cancelButtonProps = {
      startIcon: cancelIcon?.position === 'start' && cancelIcon.icon,
      endIcon: cancelIcon?.position === 'end' && cancelIcon.icon,
    };

    const submitButtonProps = {
      startIcon: submitIcon?.position === 'start' && submitIcon.icon,
      endIcon: submitIcon?.position === 'end' && submitIcon.icon,
    };
    const submitButtonRef = useRef<HTMLButtonElement | null>(null);

    useImperativeHandle(
      ref,
      () => ({
        clickSubmit: () => {
          submitButtonRef.current?.click();
        },
      }),
      []
    );

    return (
      <Dialog
        data-testid={dataTestIds?.dialog}
        disableScrollLock
        disableEnforceFocus
        open={open}
        onClose={handleClose}
        fullWidth={fullWidth}
        maxWidth={maxWidth ?? 'sm'}
        sx={sx}
        className={className}
        mobileApp={mobileApp}
        fullScreen={fullScreen}>
        {mobileApp && <HandleBar />}
        <Header>
          <Typography variant="h5" sx={{ fontWeight: 700, fontSize: mobileApp ? '1.25rem' : '1.5rem', textAlign: 'center' }}>
            {title}
          </Typography>
        </Header>

        <Form
          onSubmit={(e) => {
            e.stopPropagation();
            handleSubmit(e);
          }}>
          <Content mobileApp={mobileApp}>{children}</Content>
          <ButtonsWrapper mobileApp={mobileApp}>
            {Boolean(onCancel) && (
              <Button
                variant="outlined"
                onClick={handleCancel}
                {...cancelButtonProps}
                id="cancel-button"
                data-testid={dataTestIds?.cancelButton}
                sx={{ borderRadius: 20, px: mobileApp ? 4 : 3, py: 1, textTransform: 'none', fontWeight: 600, flex: mobileApp ? 1 : undefined }}>
                {cancelText ?? t('common.cancel')}
              </Button>
            )}
            {Boolean(onSubmit) && (
              <Button
                ref={submitButtonRef}
                id="submit-button"
                data-testid={dataTestIds?.submitButton ?? 'modal-button-submit'}
                variant="contained"
                loading={isSubmitting}
                disabled={isOnSubmitDisabled}
                type="submit"
                {...submitButtonProps}
                sx={{ borderRadius: 20, px: mobileApp ? 4 : 3, py: 1, textTransform: 'none', fontWeight: 600, flex: mobileApp ? 1 : undefined }}>
                {submitText ?? t('common.save')}
              </Button>
            )}
          </ButtonsWrapper>
        </Form>
      </Dialog>
    );
  }
);

export const ActionModal = memo(ActionModalBase) as typeof ActionModalBase;
